'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';

interface CountryIntroductionSectionProps {
    translationNamespace: string;
    countryImage: string;
}

export function CountryIntroductionSection({
    translationNamespace,
    countryImage,
}: CountryIntroductionSectionProps) {
    const t = useTranslations(translationNamespace);
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    return (
        <SectionWrapper 
            ref={sectionRef.ref}
            variant="default" 
            padding="md" 
            className="relative overflow-hidden"
        >
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className={`relative z-10 container mx-auto transition-all duration-700 ${
                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}>
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div className={`transition-all duration-700 ${
                        sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                    }`} style={{ transitionDelay: '200ms' }}>
                        <div className="flex items-center gap-4 mb-6">
                            {(() => {
                                const flagImage = t('intro.flagImage');
                                // Ensure flagImage is a valid path (starts with / or http:// or https://)
                                // If it's a translation key (contains dots/namespace), use fallback
                                const isValidImagePath = flagImage && (
                                    flagImage.startsWith('/') || 
                                    flagImage.startsWith('http://') || 
                                    flagImage.startsWith('https://')
                                );
                                
                                if (!isValidImagePath) {

                                    return null; // Don't render image if path is invalid
                                }
                                
                                return (
                                    <Image
                                        src={flagImage}
                                        alt={t('intro.flagAlt')}
                                        width={60}
                                        height={40}
                                        className="rounded-md object-cover"
                                    />
                                );
                            })()}
                            <Typography variant="h2" className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                                {t('intro.title')}
                            </Typography>
                        </div>
                        <Typography variant="p" className="text-lg text-gray-700 dark:text-gray-300 mb-6">
                            {t('intro.description')}
                        </Typography>
                        <Typography variant="p" className="text-lg text-gray-700 dark:text-gray-300">
                            {t('intro.description2')}
                        </Typography>
                    </div>
                    <div className={`relative transition-all duration-700 ${
                        sectionRef.isVisible ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-6 scale-95'
                    }`} style={{ transitionDelay: '400ms' }}>
                        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-lg">
                            <Image
                                src={countryImage}
                                alt={t('intro.countryImageAlt')}
                                width={600}
                                height={400}
                                className="w-full h-auto rounded-lg object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

